import { WorkspaceT } from '@/pages/workgroup/type';
import type { OnboardingPath } from '@/utils/analytics/types';
import { atom, useAtom } from 'jotai';
import { DataFormat, JSONParser, LoadingJobData, TabularParser } from '@tigergraph/tools-models/loading-job';
import { SampleData } from '@/pages/ingestion/loadData/configureFile/DataSourceFormat';
import { GSQLGraphJson } from '@tigergraph/tools-models/topology';
import { GraphSchema, LoadingJobConfig } from './utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useWorkspaceContext } from '@/contexts/workspaceContext';
import { toast } from 'react-hot-toast';

export const defaultOnboardingState: {
  workspace: WorkspaceT | null;
  onboardingPath: OnboardingPath;
  dataParsers: { [key: string]: TabularParser | JSONParser };
  sampleDataMap: { [key: string]: SampleData };
  dataSourceNameMap: { [key: string]: string[] };
  excludeFileNames: { [key: string]: boolean };
  dataFormatMap: { [key: string]: DataFormat };
  rawSampleDataMap: { [key: string]: string };
  fileProgressMap: { [fileName: string]: number };
  threadId: string | null;
  graph_name: string | null;
  schemaData: { graph_schema?: GraphSchema; loading_job_config?: LoadingJobConfig } | null;
  transformedSchemaData: { gsqlGraphJson: GSQLGraphJson; loadingJobs: LoadingJobData[] } | null;
  algorithmResult: { title: string; desc: string }[];
} = {
  workspace: null,
  // Overwritten by the Get Started card selection; own_data is the default because the
  // preloaded-solution path navigates straight to Explore instead of these stages.
  onboardingPath: 'own_data',
  dataParsers: {},
  sampleDataMap: {},
  rawSampleDataMap: {},
  dataSourceNameMap: {},
  excludeFileNames: {},
  dataFormatMap: {},
  fileProgressMap: {},
  threadId: null,
  graph_name: null,
  schemaData: null,
  transformedSchemaData: {
    gsqlGraphJson: {
      GraphName: '',
      VertexTypes: [],
      EdgeTypes: [],
    },
    loadingJobs: [],
  },
  algorithmResult: [],
};

export const onboardingAtom = atom(defaultOnboardingState);

export const filterByExcludeFileNames = (
  dataMap: { [key: string]: object | string },
  excludeFileNames: { [key: string]: boolean }
) => {
  return Object.keys(dataMap).reduce((prev, dataSourceName) => {
    if (!excludeFileNames[dataSourceName]) {
      prev[dataSourceName] = dataMap[dataSourceName];
    }
    return prev;
  }, {} as { [key: string]: object | string });
};

// Derived atom to compute dataSourceURIMap from dataSourceNameMap
export const dataSourceURIMapAtom = atom((get) => {
  const { dataSourceNameMap } = get(onboardingAtom);
  return Object.keys(dataSourceNameMap).reduce((prev, dataSourceName) => {
    dataSourceNameMap[dataSourceName].forEach((uri: string) => {
      if (uri.length > 0) {
        prev[uri] = dataSourceName;
      }
    });
    return prev;
  }, {} as { [uri: string]: string });
});

// Derived atom to get data source names
export const dataSourceNamesAtom = atom((get) => {
  const { dataSourceNameMap } = get(onboardingAtom);
  return Object.keys(dataSourceNameMap);
});

// Derived atom to get exclude file names
export const excludeFileNamesAtom = atom((get) => {
  const { excludeFileNames } = get(onboardingAtom);
  return excludeFileNames;
});

// this is used to validate the workspace id in the url
// if the workspace id is not in the list of workspaces, we redirect to the home page
// if the workspace id is in the list of workspaces, we set the workspace in the onboarding state
export function useOnBoardingWorkspaceValidation(fallbackToImportCSV: boolean = false) {
  const [searchParams] = useSearchParams();
  const { workspaces } = useWorkspaceContext();
  const navigate = useNavigate();
  const [{ workspace }, setOnboardingState] = useAtom(onboardingAtom);

  useEffect(() => {
    // no workspace, fallback to import csv
    if (!workspace && fallbackToImportCSV) {
      navigate(`/onboarding/import-csv?${searchParams.toString()}`, { replace: true });
    }
  }, [fallbackToImportCSV, workspace, navigate, searchParams]);

  useEffect(() => {
    if (fallbackToImportCSV) {
      return;
    }

    const urlWorkspaceId = searchParams.get('workspace_id');
    if (workspace && workspace.workspace_id === urlWorkspaceId) {
      return;
    }

    if (!urlWorkspaceId) {
      toast.error('Workspace not found');
      navigate('/', { replace: true });
      return;
    }

    if (workspaces.length > 0) {
      const workspace = workspaces.find((workspace) => workspace.workspace_id === urlWorkspaceId);
      if (!workspace) {
        toast.error('Workspace not found');
        navigate('/', { replace: true });
      } else {
        // initialize the onboarding state with the workspace
        setOnboardingState({
          ...defaultOnboardingState,
          workspace: workspace,
        });
      }
    }
  }, [workspaces, searchParams, workspace, setOnboardingState, navigate, fallbackToImportCSV]);
}
